import { useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Award, Loader2, AlertCircle, CalendarCheck, TrendingUp, Star,
  Upload, CheckCircle2, X, Clock, FileText, Plus, Building2, Search,
} from "lucide-react";
import { useGetMyCpdLedgerQuery } from "../../events/api/eventsApi";
import {
  useGetCpdEligibleEventsQuery,
  useGetMyCpdSubmissionsQuery,
  useSubmitExternalCpdMutation,
} from "../api/cpdApi";

function fmt(d?: string | null) {
  if (!d) return "—";
  return new Date(d).toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

const TYPE_BADGE: Record<string, string> = {
  event:     "bg-indigo-50 text-indigo-700 border-indigo-100",
  manual:    "bg-emerald-50 text-emerald-700 border-emerald-100",
  deduction: "bg-rose-50 text-rose-700 border-rose-100",
};

const SOURCE_BADGE: Record<string, { cls: string; label: string }> = {
  auto:   { cls: "bg-teal-50 text-teal-700 border-teal-100",     label: "Auto" },
  admin:  { cls: "bg-indigo-50 text-indigo-700 border-indigo-100", label: "Admin" },
  system: { cls: "bg-gray-50 text-gray-500 border-gray-100",      label: "System" },
};

const STATUS_BADGE: Record<string, { cls: string; label: string }> = {
  processing: { cls: "bg-amber-50 text-amber-700 border-amber-200",     label: "Under Review" },
  approved:   { cls: "bg-emerald-50 text-emerald-700 border-emerald-200", label: "Approved" },
  rejected:   { cls: "bg-rose-50 text-rose-700 border-rose-200",         label: "Rejected" },
};

type Tab = "ledger" | "submissions";

function extractError(err: any): string {
  if (err?.data?.errors) {
    const first = Object.values(err.data.errors)[0];
    return Array.isArray(first) ? (first[0] as string) : String(first);
  }
  return err?.data?.message ?? err?.data?.error ?? "Something went wrong";
}

export default function MyCpdPage() {
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState<Tab>(
    searchParams.get("tab") === "submissions" ? "submissions" : "ledger"
  );
  const [showSubmitForm, setShowSubmitForm] = useState(false);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [notes, setNotes]                     = useState("");
  const [certFile, setCertFile]               = useState<File | null>(null);
  const [formError, setFormError]             = useState("");
  const [successMsg, setSuccessMsg]           = useState("");
  const [eventSearch, setEventSearch]         = useState("");
  const fileRef = useRef<HTMLInputElement>(null);

  const { data: ledgerData, isLoading: loadingLedger, isError: ledgerError } = useGetMyCpdLedgerQuery();
  const { data: submissions = [], isLoading: loadingSubs }                    = useGetMyCpdSubmissionsQuery();
  const { data: eligibleEvents = [], isLoading: loadingEvents }               = useGetCpdEligibleEventsQuery();
  const [submitCpd, { isLoading: submitting }]                                = useSubmitExternalCpdMutation();

  const entries: any[] = ledgerData?.data ?? [];
  const total: number  = ledgerData?.total ?? 0;

  const filteredEvents = (eligibleEvents as any[]).filter((e: any) =>
    !eventSearch ||
    e.title?.toLowerCase().includes(eventSearch.toLowerCase()) ||
    e.host_name?.toLowerCase().includes(eventSearch.toLowerCase())
  );

  const selectedEvent = (eligibleEvents as any[]).find((e: any) => String(e.id) === selectedEventId);

  const openForm = () => {
    setSelectedEventId(""); setNotes(""); setCertFile(null);
    setFormError(""); setEventSearch(""); setShowSubmitForm(true);
  };

  const handleSubmit = async () => {
    setFormError("");
    if (!selectedEventId)  { setFormError("Please select an event"); return; }
    if (!certFile)         { setFormError("Please upload your attendance certificate"); return; }

    const fd = new FormData();
    fd.append("event_id",    selectedEventId);
    fd.append("certificate", certFile);
    if (notes) fd.append("notes", notes);

    try {
      const res = await submitCpd(fd).unwrap();
      setSuccessMsg(res?.message ?? "Submission received!");
      setShowSubmitForm(false);
      setTab("submissions");
    } catch (err) {
      setFormError(extractError(err));
    }
  };

  const inputCls = "w-full border-b-2 border-slate-100 py-2 font-bold text-slate-800 outline-none focus:border-slate-900 bg-transparent text-sm";

  return (
    <div className="min-h-screen bg-[#FDFDFD] pb-32">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10 py-12">

        {/* HEADER */}
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
          <div>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-4">My Profile / CPD</p>
            <h1 className="text-5xl lg:text-6xl font-[1000] text-slate-900 tracking-tight leading-none">
              My CPD<span className="text-primary italic">.</span>
            </h1>
            <p className="text-slate-500 text-lg mt-4 font-medium max-w-xl">
              Track your continuing professional development points and claim CPD from chapter or accredited institute events.
            </p>
          </div>
          <button
            onClick={openForm}
            className="flex items-center gap-2 bg-slate-900 text-white font-black text-sm px-6 py-3.5 rounded-2xl hover:bg-primary transition-all shadow-xl shadow-slate-200"
          >
            <Plus className="w-4 h-4" /> Claim CPD Points
          </button>
        </header>

        {/* STATS */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          {[
            { label: "Total CPD Points",    val: total.toFixed(1),                                                                    icon: Award,         color: "text-violet-600 bg-violet-50" },
            { label: "Total Entries",       val: entries.length,                                                                       icon: TrendingUp,    color: "text-indigo-600 bg-indigo-50" },
            { label: "From Events",         val: entries.filter((e) => e.activity_type === "event").length,                            icon: CalendarCheck,  color: "text-emerald-600 bg-emerald-50" },
            { label: "Under Review",        val: (submissions as any[]).filter((s: any) => s.status === "processing").length,          icon: Clock,         color: "text-amber-600 bg-amber-50" },
          ].map((s, i) => (
            <div key={i} className="bg-white border border-slate-100 p-6 rounded-[2rem] shadow-sm flex items-center gap-4">
              <div className={`w-11 h-11 rounded-2xl flex items-center justify-center ${s.color} shrink-0`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">{s.label}</div>
                <div className="text-xl font-black text-slate-900">{s.val}</div>
              </div>
            </div>
          ))}
        </div>

        {/* SUCCESS TOAST */}
        {successMsg && (
          <div className="mb-6 flex items-start gap-3 px-5 py-4 rounded-2xl border bg-emerald-50 border-emerald-100 text-emerald-700 text-sm font-bold">
            <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" />
            <span className="flex-1">{successMsg}</span>
            <button onClick={() => setSuccessMsg("")}><X className="w-4 h-4" /></button>
          </div>
        )}

        {/* TABS */}
        <div className="flex gap-2 mb-8">
          {([
            { key: "ledger",      label: "My CPD Ledger" },
            { key: "submissions", label: "CPD Claims" },
          ] as { key: Tab; label: string }[]).map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-5 py-2.5 rounded-2xl text-xs font-black uppercase tracking-widest transition-colors ${
                tab === t.key ? "bg-slate-900 text-white" : "bg-white border border-slate-100 text-slate-500 hover:border-slate-300"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* ── TAB: LEDGER ── */}
        {tab === "ledger" && (
          <>
            {loadingLedger ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-indigo-500 animate-spin" /></div>
            ) : ledgerError ? (
              <div className="bg-rose-50 border border-rose-100 rounded-[2rem] p-8 flex items-center gap-4">
                <AlertCircle className="w-6 h-6 text-rose-500 shrink-0" />
                <p className="text-rose-700 font-bold">Failed to load CPD record.</p>
              </div>
            ) : entries.length === 0 ? (
              <div className="bg-white border border-slate-100 rounded-[2rem] p-16 text-center">
                <Award className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 font-bold">No CPD entries yet.</p>
                <p className="text-slate-400 text-sm mt-1">Attend CPD-tagged events or claim CPD from a chapter / accredited institute event.</p>
              </div>
            ) : (
              <div className="bg-white border border-slate-100 rounded-[2rem] overflow-hidden">
                <div className="p-6 border-b border-slate-50 flex items-center justify-between">
                  <div className="text-[10px] font-black text-slate-400 uppercase tracking-widest">CPD Ledger</div>
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4 text-violet-500" />
                    <span className="font-black text-slate-900">{total.toFixed(1)} pts total</span>
                  </div>
                </div>
                <div className="divide-y divide-slate-50">
                  {entries.map((entry: any) => {
                    const src = entry.allocation_source ? SOURCE_BADGE[entry.allocation_source] : null;
                    return (
                      <div key={entry.id} className="flex items-start justify-between p-5 hover:bg-slate-50/50 transition-colors">
                        <div className="flex items-start gap-3">
                          <div className={`w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5 ${
                            entry.activity_type === "deduction" ? "bg-rose-50 text-rose-600" : "bg-violet-50 text-violet-600"
                          }`}>
                            <Award className="w-4 h-4" />
                          </div>
                          <div>
                            <div className="font-bold text-slate-900 text-sm">
                              {entry.event?.title ?? entry.notes ?? `CPD Entry #${entry.id}`}
                            </div>
                            <div className="flex items-center gap-2 mt-1.5">
                              <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg border ${TYPE_BADGE[entry.activity_type] ?? "bg-slate-50 text-slate-500 border-slate-100"}`}>
                                {entry.activity_type}
                              </span>
                              {src && (
                                <span className={`text-[10px] font-black uppercase tracking-widest px-2 py-0.5 rounded-lg border ${src.cls}`}>
                                  {src.label}
                                </span>
                              )}
                              <span className="text-[10px] text-slate-400 font-medium">{fmt(entry.activity_date)}</span>
                            </div>
                            {entry.authorized_by && (
                              <div className="text-[10px] text-slate-400 font-medium mt-1">
                                Awarded by <span className="text-slate-600 font-bold">{entry.authorized_by}</span>
                              </div>
                            )}
                          </div>
                        </div>
                        <div className={`font-black text-base shrink-0 ml-4 ${
                          entry.activity_type === "deduction" ? "text-rose-600" : "text-violet-600"
                        }`}>
                          {entry.activity_type === "deduction" ? "−" : "+"}{Math.abs(entry.points).toFixed(1)}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        {/* ── TAB: CLAIMS ── */}
        {tab === "submissions" && (
          <>
            {loadingSubs ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 text-indigo-500 animate-spin" /></div>
            ) : (submissions as any[]).length === 0 ? (
              <div className="bg-white border border-slate-100 rounded-[2rem] p-16 text-center">
                <FileText className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-400 font-bold">No CPD claims yet.</p>
                <p className="text-slate-400 text-sm mt-1">
                  Attended a chapter or accredited institute CPD event? Claim your points.
                </p>
                <button
                  onClick={openForm}
                  className="mt-6 inline-flex items-center gap-2 bg-slate-900 text-white font-black text-sm px-6 py-3 rounded-2xl hover:bg-primary transition-all"
                >
                  <Plus className="w-4 h-4" /> Claim CPD Points
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {(submissions as any[]).map((s: any) => {
                  const st = STATUS_BADGE[s.status] ?? { cls: "bg-slate-100 text-slate-500 border-slate-200", label: s.status };
                  return (
                    <div key={s.id} className="bg-white border border-slate-100 rounded-[2rem] p-6 hover:border-slate-300 transition-colors">
                      <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-2xl bg-slate-50 flex items-center justify-center shrink-0">
                            <CalendarCheck className="w-5 h-5 text-indigo-400" />
                          </div>
                          <div>
                            <h3 className="font-[900] text-slate-900 text-base leading-tight">
                              {s.event?.title ?? s.title}
                            </h3>
                            <p className="text-sm text-slate-500 font-medium mt-0.5">{s.organizer}</p>
                            <div className="flex flex-wrap items-center gap-3 mt-2">
                              <span className={`text-[10px] font-black uppercase tracking-widest px-2.5 py-1 rounded-lg border ${st.cls}`}>
                                {st.label}
                              </span>
                              <span className="text-xs text-slate-400 font-medium">{fmt(s.activity_date)}</span>
                              {s.event?.cpd_points != null && (
                                <span className="text-xs font-black text-slate-500">{s.event.cpd_points} pts</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="shrink-0 text-right space-y-1">
                          {s.status === "approved" && s.cpd_points_awarded != null && (
                            <div className="text-2xl font-[1000] text-emerald-600">+{s.cpd_points_awarded}</div>
                          )}
                        </div>
                      </div>
                      {(s.admin_notes || s.reviewed_by) && (
                        <div className={`mt-4 px-4 py-3 rounded-2xl text-sm ${
                          s.status === "rejected" ? "bg-rose-50 text-rose-700" : "bg-slate-50 text-slate-600"
                        }`}>
                          {s.admin_notes && <p className="font-medium">{s.admin_notes}</p>}
                          {s.reviewed_by && (
                            <p className="text-xs font-black mt-1 opacity-60">Reviewed by {s.reviewed_by}</p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}

        {/* INFO FOOTER */}
        <div className="mt-10 bg-slate-900 rounded-[2.5rem] p-8 text-white">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-2xl bg-white/10 flex items-center justify-center shrink-0 mt-0.5">
              <Award className="w-5 h-5" />
            </div>
            <div>
              <div className="text-[10px] font-black uppercase tracking-widest text-white/40 mb-2">How CPD Works</div>
              <p className="font-bold text-sm text-white/80 leading-relaxed">
                <span className="text-white">NIQS events</span> — CPD is allocated automatically when attendance is marked at a completed NIQS event.<br />
                <span className="text-white mt-1 block">Chapter &amp; Accredited Institute events</span> — Select the event, upload your proof of attendance. If your attendance is already recorded, CPD is granted immediately. Otherwise, an admin reviews your evidence.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── CLAIM CPD SLIDE-IN ── */}
      {showSubmitForm && (
        <div className="fixed inset-0 z-50 flex">
          <div className="flex-1 bg-black/30 backdrop-blur-sm" onClick={() => setShowSubmitForm(false)} />
          <div className="w-full max-w-lg bg-white h-full overflow-y-auto shadow-2xl flex flex-col">
            <div className="p-7 border-b border-slate-100 flex items-center justify-between sticky top-0 bg-white z-10">
              <div>
                <h2 className="font-black text-slate-900 text-lg uppercase tracking-tight">Claim CPD Points</h2>
                <p className="text-xs text-slate-400 font-medium mt-0.5">Select an event and upload your attendance evidence</p>
              </div>
              <button onClick={() => setShowSubmitForm(false)} className="p-2 rounded-xl hover:bg-slate-50">
                <X className="w-5 h-5 text-slate-400" />
              </button>
            </div>

            <div className="p-7 space-y-5 flex-1">

              {/* Event selector */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-3 block">
                  Select Event *
                </label>

                {/* Search within event list */}
                <div className="flex items-center gap-2 border border-slate-100 rounded-xl px-3 py-2 mb-3 bg-slate-50">
                  <Search className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                  <input
                    className="flex-1 text-xs font-semibold text-slate-700 outline-none bg-transparent placeholder:text-slate-300"
                    placeholder="Search events…"
                    value={eventSearch}
                    onChange={(e) => setEventSearch(e.target.value)}
                  />
                </div>

                {loadingEvents ? (
                  <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-indigo-400 animate-spin" /></div>
                ) : filteredEvents.length === 0 ? (
                  <div className="text-center py-8 text-sm text-slate-400 font-medium border border-dashed border-slate-200 rounded-2xl">
                    {eventSearch ? "No events match your search." : "No CPD events available for claiming right now."}
                  </div>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
                    {filteredEvents.map((ev: any) => (
                      <button
                        key={ev.id}
                        onClick={() => setSelectedEventId(String(ev.id))}
                        className={`w-full text-left p-4 rounded-2xl border transition-all ${
                          selectedEventId === String(ev.id)
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-slate-100 bg-white hover:border-slate-300 text-slate-800"
                        }`}
                      >
                        <div className="font-black text-sm leading-tight">{ev.title}</div>
                        <div className={`flex items-center gap-2 mt-1 text-xs font-medium ${selectedEventId === String(ev.id) ? "text-white/60" : "text-slate-400"}`}>
                          <Building2 className="w-3 h-3 shrink-0" />
                          <span>{ev.host_name}</span>
                          <span>·</span>
                          <span>{fmt(ev.end_date)}</span>
                          <span>·</span>
                          <span className="font-black">{ev.cpd_points} pts</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {selectedEvent && (
                  <div className="mt-3 flex items-center gap-2 text-xs font-black text-emerald-700 bg-emerald-50 border border-emerald-100 px-3 py-2 rounded-xl">
                    <CheckCircle2 className="w-3.5 h-3.5 shrink-0" />
                    Selected: {selectedEvent.title} — {selectedEvent.cpd_points} pts
                  </div>
                )}
              </div>

              {/* Notes */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Additional Notes</label>
                <textarea
                  className="w-full border-b-2 border-slate-100 py-2 font-bold text-slate-800 outline-none focus:border-slate-900 bg-transparent text-sm resize-none"
                  rows={2}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Any additional context for the reviewer (optional)…"
                />
              </div>

              {/* Certificate upload */}
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2 block">
                  Proof of Attendance * <span className="normal-case font-medium text-slate-300">(PDF / Image, max 5 MB)</span>
                </label>
                <input
                  ref={fileRef}
                  type="file"
                  accept=".pdf,.jpg,.jpeg,.png"
                  className="hidden"
                  onChange={(e) => setCertFile(e.target.files?.[0] ?? null)}
                />
                <button
                  type="button"
                  onClick={() => fileRef.current?.click()}
                  className="w-full border-2 border-dashed border-slate-200 rounded-2xl py-6 flex flex-col items-center gap-2 hover:border-slate-400 transition-colors"
                >
                  <Upload className="w-6 h-6 text-slate-400" />
                  {certFile ? (
                    <span className="text-sm font-black text-slate-700">{certFile.name}</span>
                  ) : (
                    <span className="text-sm font-bold text-slate-400">Click to upload certificate / evidence</span>
                  )}
                </button>
                {certFile && (
                  <button onClick={() => setCertFile(null)} className="mt-2 text-xs font-black text-rose-500 hover:text-rose-700">
                    Remove file
                  </button>
                )}
              </div>

              {formError && (
                <div className="flex items-center gap-2 bg-rose-50 border border-rose-100 text-rose-700 text-sm font-medium px-4 py-3 rounded-2xl">
                  <AlertCircle className="w-4 h-4 shrink-0" /> {formError}
                </div>
              )}

              {/* Info box */}
              <div className="bg-indigo-50 border border-indigo-100 rounded-2xl px-4 py-3 text-xs text-indigo-700 font-medium leading-relaxed">
                If your attendance is already recorded in the system, CPD will be allocated immediately. Otherwise your certificate will be reviewed by an administrator.
              </div>
            </div>

            <div className="p-7 border-t border-slate-50 sticky bottom-0 bg-white">
              <button
                onClick={handleSubmit}
                disabled={submitting || !selectedEventId || !certFile}
                className="w-full bg-slate-900 text-white font-black text-sm py-3.5 rounded-2xl hover:bg-primary disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Submit Claim
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
